import { useState } from 'react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { RingGauge, rateColor } from '../components/ui/RingGauge';
import { SplitFlap } from '../components/ui/SplitFlap';
import {
  DocDisclosureButton,
  DocDisclosurePanel,
} from '../components/ui/InlineDocViewer';
import { wilsonInterval, formatWilson } from '../lib/wilson-ci';
import { fmtCdxDateTime, fmtCdxDate } from '../lib/dates';

/**
 * StyleGuide: a kitchen-sink showcase of the ClariCommand editorial design
 * system. Every core component class from src/index.css is demonstrated here,
 * followed by each copied UI primitive rendered with sample props. No data is
 * fetched; everything is in-file sample content. Use this page to eyeball the
 * full visual vocabulary in one scroll.
 */
export function StyleGuide() {
  // Disclosure state for the InlineDocViewer primitives demo.
  const [docOpen, setDocOpen] = useState(false);

  // A worked Wilson CI for the helpers row.
  const ci = wilsonInterval(214, 812);

  return (
    <div className="page-pad">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <header>
        <div className="section-eyebrow">ClariCommand · Design System</div>
        <h1 className="section-title">
          Style <em>Guide</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          A single-scroll reference for every editorial component class and copied
          UI primitive in this starter kit. Paper-and-ink surfaces, a teal accent,
          tabular figures everywhere a number appears.
        </p>
      </header>

      {/* ── Section chrome ───────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Section Chrome
          <span className="aux">eyebrow · title · lede · rule · block-head</span>
        </div>
        <div className="card">
          <div className="section-eyebrow">Section · Eyebrow</div>
          <h2 className="section-title">
            A serif <em>display</em> title
          </h2>
          <div className="section-rule" />
          <p className="section-lede" style={{ marginBottom: 0 }}>
            The lede sits beneath the rule and carries the orienting sentence for
            a section. It is capped at a comfortable measure for reading.
          </p>
        </div>
      </section>

      {/* ── Buttons ──────────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Buttons
          <span className="aux">.btn variants + .btn-sm</span>
        </div>
        <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button className="btn">Default</button>
          <button className="btn btn-primary">Primary</button>
          <button className="btn btn-teal">Teal</button>
          <button className="btn btn-ghost">Ghost</button>
          <button className="btn btn-danger">Danger</button>
          <button className="btn btn-sm">Small</button>
          <button className="btn btn-primary btn-sm">Primary Small</button>
        </div>
      </section>

      {/* ── Pills + chips ────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Pills &amp; Chips
          <span className="aux">.pill variants · .chip · .chip.selected</span>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span className="pill">Default</span>
            <span className="pill pill-success">Success</span>
            <span className="pill pill-warning">Warning</span>
            <span className="pill pill-danger">Danger</span>
            <span className="pill pill-primary">Primary</span>
            <span className="pill pill-accent">Accent</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span className="chip">Unselected</span>
            <span className="chip selected">Selected</span>
            <span className="chip">Q1 2026</span>
            <span className="chip">Q2 2026</span>
          </div>
        </div>
      </section>

      {/* ── Stat tiles ───────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Stat Tiles
          <span className="aux">.stat (.label .value .sub) + .stat.accent</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 'var(--gap-card)',
          }}
        >
          <div className="stat">
            <div className="label">Members</div>
            <div className="value tab-num">21,312</div>
            <div className="sub">roster as of {fmtCdxDate('2026-06-26')}</div>
          </div>
          <div className="stat">
            <div className="label">Kits Shipped</div>
            <div className="value tab-num">18,940</div>
            <div className="sub">+412 this week</div>
          </div>
          <div className="stat">
            <div className="label">Returned</div>
            <div className="value tab-num">6,118</div>
            <div className="sub">32.3% of shipped</div>
          </div>
          <div className="stat accent">
            <div className="label">Screen Rate</div>
            <div className="value tab-num">27.4%</div>
            <div className="sub">95% CI 24.4–30.6%</div>
          </div>
        </div>
      </section>

      {/* ── Data table ───────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Data Table
          <span className="aux">.dt · thead th · tbody td · .num-cell</span>
        </div>
        <div className="card card-flush">
          <table className="dt">
            <thead>
              <tr>
                <th>Region</th>
                <th>Plan</th>
                <th>Eligible</th>
                <th>Returned</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_ROWS.map((r) => (
                <tr key={r.region}>
                  <td>{r.region}</td>
                  <td>{r.plan}</td>
                  <td className="num-cell">{r.eligible.toLocaleString()}</td>
                  <td className="num-cell">{r.returned.toLocaleString()}</td>
                  <td className="num-cell">
                    {((r.returned / r.eligible) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Funnel ───────────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Funnel
          <span className="aux">.funnel-row · .funnel-bar · .funnel-fill</span>
        </div>
        <div className="card">
          {SAMPLE_FUNNEL.map((f) => (
            <div className="funnel-row" key={f.stage}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                {f.stage}
              </div>
              <div className="funnel-bar">
                <div className="funnel-fill" style={{ width: `${f.pct}%` }}>
                  {f.pct}%
                </div>
              </div>
              <div className="num-cell mono">{f.n.toLocaleString()}</div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-400)' }}>
                {f.note}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Notes ────────────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Note Callouts
          <span className="aux">.note · .note.warn · .note.danger</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="note">
            <b>Default note.</b> Wilson confidence intervals are shown on every
            rate KPI. Tabular figures keep columns aligned.
          </div>
          <div className="note warn">
            <b>Warning.</b> Last data refresh is more than 24 hours old. Numbers
            below may be stale.
          </div>
          <div className="note danger">
            <b>Blocked.</b> The upstream feed failed its last sync. Showing the
            most recent good snapshot.
          </div>
        </div>
      </section>

      {/* ── Leader rows ──────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Leader Rows
          <span className="aux">.leader-row (.k .leader .v)</span>
        </div>
        <div className="card">
          {SAMPLE_LEADERS.map((l) => (
            <div className="leader-row" key={l.k}>
              <span className="k">{l.k}</span>
              <span className="leader" />
              <span className="v">{l.v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Numeric display ──────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Numeric Display
          <span className="aux">.numeric-display · .numeric-heading</span>
        </div>
        <div className="card" style={{ display: 'flex', gap: 40, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <div>
            <div className="micro">Headline</div>
            <div className="numeric-display">27.4%</div>
          </div>
          <div>
            <div className="micro">Heading</div>
            <div className="numeric-heading">$1,284,902</div>
          </div>
        </div>
      </section>

      {/* ── Form inputs ──────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Form Inputs
          <span className="aux">.field · .field-row · .field-label · inputs</span>
        </div>
        <div className="card">
          <div className="field-row">
            <div>
              <label className="field-label" htmlFor="sg-first">First name</label>
              <input id="sg-first" className="text-input" placeholder="Jane" defaultValue="Jane" />
            </div>
            <div>
              <label className="field-label" htmlFor="sg-last">Last name</label>
              <input id="sg-last" className="text-input" placeholder="Doe" defaultValue="Doe" />
            </div>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="sg-region">Region</label>
            <select id="sg-region" className="select-input" defaultValue="orange">
              <option value="orange">Orange County</option>
              <option value="la">Los Angeles</option>
              <option value="riverside">Riverside</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field-label" htmlFor="sg-notes">Notes</label>
            <textarea
              id="sg-notes"
              className="textarea-input"
              rows={3}
              defaultValue="Member requested a replacement kit on the last call."
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          COPIED UI PRIMITIVES, each rendered with sample props
          ══════════════════════════════════════════════════════════ */}
      <section>
        <div className="block-head">
          UI Primitives
          <span className="aux">copied from the portal · sample props</span>
        </div>

        {/* RingGauge ─────────────────────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 'var(--gap-card)' }}>
          <div className="micro" style={{ marginBottom: 12 }}>RingGauge</div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <RingGauge pct={27.4} value="27.4%" caption="Screen rate" />
            <RingGauge pct={62} value="62%" caption="Eligible reached" />
            <RingGauge
              pct={9.5}
              value="9.5%"
              caption="Below benchmark"
              color={rateColor(9.5)}
            />
            <RingGauge pct={null} value="--" caption="No data yet" />
          </div>
        </div>

        {/* SplitFlap ─────────────────────────────────────────────────── */}
        <div
          className="card"
          style={{
            marginBottom: 'var(--gap-card)',
            background: 'var(--ink-900)',
            borderColor: 'var(--ink-700)',
          }}
        >
          <div className="micro" style={{ marginBottom: 12, color: 'var(--paper-2)' }}>
            SplitFlap
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
            <SplitFlap value="FLIGHT DECK" size="lg" aria-label="Flight Deck" />
            <SplitFlap value="27.4%" size="hero" charSet="numeric" accent aria-label="27.4 percent" />
            <SplitFlap value="LOADING" size="md" idle aria-label="Loading" />
          </div>
        </div>

        {/* LoadingSpinner ────────────────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 'var(--gap-card)' }}>
          <div className="micro" style={{ marginBottom: 12 }}>LoadingSpinner</div>
          <LoadingSpinner label="Loading sample data…" />
        </div>

        {/* InlineDocViewer (disclosure primitives) ───────────────────── */}
        <div className="card">
          <div className="micro" style={{ marginBottom: 12 }}>
            InlineDocViewer · DocDisclosureButton + DocDisclosurePanel
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DocDisclosureButton
              open={docOpen}
              onToggle={() => setDocOpen((o) => !o)}
              label="result report"
            />
            <span style={{ fontSize: 13, color: 'var(--ink-900)' }}>
              Result report · {fmtCdxDateTime('2026-06-24 14:08')}
            </span>
          </div>
          {docOpen && (
            <DocDisclosurePanel>
              <div className="note">
                In the live portal an <b>InlineDocFrame</b> would render the document
                here. It runs a same-origin <span className="mono">fetch()</span>{' '}
                preflight against an <span className="mono">/api/*</span> asset URL, so
                in a static kit it shows its error state. The two presentational
                exports above are the standalone primitives.
              </div>
            </DocDisclosurePanel>
          )}
        </div>
      </section>

      {/* ── Helper functions ─────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Helpers
          <span className="aux">wilson-ci · dates</span>
        </div>
        <div className="card">
          <div className="leader-row">
            <span className="k">formatWilson(214 / 812)</span>
            <span className="leader" />
            <span className="v">{formatWilson(ci)}</span>
          </div>
          <div className="leader-row">
            <span className="k">fmtCdxDateTime('2026-06-24 14:08')</span>
            <span className="leader" />
            <span className="v">{fmtCdxDateTime('2026-06-24 14:08')}</span>
          </div>
          <div className="leader-row">
            <span className="k">fmtCdxDate('6/24/2026')</span>
            <span className="leader" />
            <span className="v">{fmtCdxDate('6/24/2026')}</span>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="editorial-footer" style={{ marginTop: 32 }}>
        <span>ClariCommand Starter Kit</span>
        <span>·</span>
        <span>Style Guide</span>
      </footer>
    </div>
  );
}

/* ── In-file sample data (no fetches) ───────────────────────────────── */

const SAMPLE_ROWS: { region: string; plan: string; eligible: number; returned: number }[] = [
  { region: 'Orange County', plan: 'HMO', eligible: 8412, returned: 2611 },
  { region: 'Los Angeles', plan: 'HMO', eligible: 6190, returned: 1702 },
  { region: 'Riverside', plan: 'PPO', eligible: 3104, returned: 884 },
  { region: 'San Bernardino', plan: 'PPO', eligible: 2206, returned: 521 },
  { region: 'San Diego', plan: 'HMO', eligible: 1400, returned: 400 },
];

const SAMPLE_FUNNEL: { stage: string; pct: number; n: number; note: string }[] = [
  { stage: 'ELIGIBLE', pct: 100, n: 21312, note: 'roster' },
  { stage: 'SHIPPED', pct: 89, n: 18940, note: 'kits out' },
  { stage: 'DELIVERED', pct: 81, n: 17269, note: 'USPS' },
  { stage: 'RETURNED', pct: 29, n: 6118, note: 'inbound' },
  { stage: 'RESULTED', pct: 27, n: 5840, note: 'lab' },
];

const SAMPLE_LEADERS: { k: string; v: string }[] = [
  { k: 'Campaign', v: '26 FOBT' },
  { k: 'Tenant', v: 'Alignment' },
  { k: 'Data through', v: '2026-06-26' },
  { k: 'Screen rate', v: '27.4%' },
  { k: 'Wilson 95% CI', v: '24.4–30.6%' },
];
